import type { View } from '@clawharbor/manifest';
import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import type { DashboardActions, DashboardState } from '../store.js';
import { viewDraftOf } from '../store.js';
import { Empty, ErrorNote } from './bits.js';

/**
 * One view, as its own editable card.
 *
 * It was inline in the list below until a tool's own Appearance panel needed
 * the same thing: a person configuring a tool should be able to see and fix the
 * slot that feeds it without leaving the tool. Two copies of a slot editor is
 * how the two would disagree, so there is one, and both callers mount it.
 */
export function ViewCard(props: {
  view: View;
  state: DashboardState;
  actions: DashboardActions;
}): VNode {
  const { view: v, state, actions } = props;
  const d = viewDraftOf(state, v);
              const saving = state.savingView[v.id] === true;
              const slots = Object.keys(d.slots);
  return html`
                <li class="vcard">
                  <div class="vcard__top">
                    <h3 class="vcard__id">${v.id}</h3>
                    <label class="vcard__layout" for=${`layout-${v.id}`}>
                      <span class="cardlabel">Layout</span>
                      <select
                        id=${`layout-${v.id}`}
                        class="vcard__select"
                        disabled=${saving}
                        onChange=${(e: Event) =>
                          actions.editView(v.id, {
                            layout: (e.target as HTMLSelectElement).value as View['layout'],
                          })}
                      >
                        ${LAYOUTS.map(
                          (l) => html`<option value=${l} selected=${l === d.layout}>${l}</option>`,
                        )}
                      </select>
                    </label>
                  </div>

                  <div class="vcard__grid">
                    <label class="vfield" for=${`vtitle-${v.id}`}>
                      <span class="cardlabel">Title</span>
                      <input
                        id=${`vtitle-${v.id}`}
                        class="vfield__input"
                        type="text"
                        value=${d.title}
                        disabled=${saving}
                        onInput=${(e: Event) =>
                          actions.editView(v.id, { title: (e.target as HTMLInputElement).value })}
                      />
                    </label>
                    <label class="vfield" for=${`vlink-${v.id}`}>
                      <span class="cardlabel">Link field</span>
                      <input
                        id=${`vlink-${v.id}`}
                        class="vfield__input"
                        type="text"
                        placeholder="deepLink"
                        value=${d.linkField}
                        disabled=${saving}
                        onInput=${(e: Event) =>
                          actions.editView(v.id, { linkField: (e.target as HTMLInputElement).value })}
                      />
                      <span class="vfield__hint">
                        The row field holding the outbound link back to the origin.
                      </span>
                    </label>
                    <label class="vfield" for=${`vbadges-${v.id}`}>
                      <span class="cardlabel">Badges</span>
                      <input
                        id=${`vbadges-${v.id}`}
                        class="vfield__input"
                        type="text"
                        placeholder="format, language"
                        value=${d.badges}
                        disabled=${saving}
                        onInput=${(e: Event) =>
                          actions.editView(v.id, { badges: (e.target as HTMLInputElement).value })}
                      />
                      <span class="vfield__hint">Comma-separated row fields, in order.</span>
                    </label>
                  </div>

                  <p class="cardlabel">Slots</p>
                  ${slots.length
                    ? html`<div class="slots">
                        ${slots.map(
                          (name) => html`
                            <label class="slot" for=${`slot-${v.id}-${name}`}>
                              <span class="slot__name">${name}</span>
                              <input
                                id=${`slot-${v.id}-${name}`}
                                class="slot__input"
                                type="text"
                                value=${d.slots[name] ?? ''}
                                disabled=${saving}
                                onInput=${(e: Event) =>
                                  actions.editSlot(
                                    v.id,
                                    name,
                                    (e.target as HTMLInputElement).value,
                                  )}
                              />
                            </label>
                          `,
                        )}
                      </div>`
                    : Empty({
                        title: 'No slots.',
                        body: 'This layout renders whatever fields the rows carry, in their own order. Binding a slot is how a field gets a fixed place in the card.',
                      })}

                  <div class="vcard__actions">
                    <button
                      type="button"
                      class="btn"
                      disabled=${saving}
                      onClick=${() => actions.saveView(v.id)}
                    >
                      ${saving ? 'Saving…' : `Save ${v.id}`}
                    </button>
                    ${v.emptyText
                      ? html`<span class="vcard__empty"
                          >Empty state:${SP}<i>${v.emptyText}</i></span
                        >`
                      : null}
                  </div>
                  ${ErrorNote({ message: state.viewErrors[v.id] ?? null })}
                </li>`;
}

/**
 * The views, edited as what they are: a layout plus a slot map.
 *
 * A view is how one capability's rows are painted on BOTH surfaces — the branded
 * page and the widget inside a chat client — by `renderView` in render-core, with
 * no tenant-specific code anywhere. So a slot is a field path or a template over a
 * row, and editing one changes what a person sees without touching the binding
 * that fetched it. That separation is worth exposing rather than hiding behind a
 * "theme" abstraction: an operator whose titles are blank is looking for a slot
 * bound to a field the rows do not have, and this is the screen where they find it.
 */

const LAYOUTS: ReadonlyArray<View['layout']> = [
  'list',
  'grid',
  'table',
  'detail',
  'timeline',
  'form',
  'empty',
];

export function ViewsSection(props: {
  views: View[];
  state: DashboardState;
  actions: DashboardActions;
}): VNode {
  const { views, state, actions } = props;

  return html`
    <section class="vsec" aria-labelledby="views-h">
      <h2 class="sec__h" id="views-h">Views</h2>
      <p class="sec__p">
        One view per capability, rendered by the same emitter on the branded page and inside a chat
        client. A slot names a field on the returned rows, or a template over one — a slot bound to
        a field the rows do not carry renders empty on both surfaces, which is what an empty title
        in a run result above usually means.
      </p>

      ${views.length
        ? html`<ul class="vlist">
            ${views.map((v) => ViewCard({ view: v, state, actions }))}
          </ul>`
        : Empty({
            title: 'This manifest declares no views.',
            body: 'Every tool answers as plain rows, which is a legitimate way to publish and a plain one. A view is declared when the manifest is published, not from here.',
          })}
    </section>
  `;
}
